/**
 * ParentSessionViewPage — parent's read-only view of a child's session transcript.
 *
 * Opened from the "View Session" button on each row of the parent dashboard's
 * Reports tab. Exists because the generated HTML report no longer embeds the
 * per-question conversation transcript; parents read the transcript here instead,
 * while the report carries the model answer and the student's final answer.
 *
 * Deliberately NOT a reuse of SessionChatPage. That page is built around the
 * student-as-viewer assumption — it renders the logged-in user's pebbles chip and
 * profile chip, a "Back" link to the student /sessions list, the student completion
 * summary, and demo-limit logic keyed to useAuth().user.userId. A parent opening it
 * would see their OWN pebbles and avatar against their child's transcript. This page
 * renders the transcript and nothing else, and cannot regress the live student chat.
 *
 * Authorization: GET /sessions/{sessionId}/messages already authorizes parents via
 * authorize_session_access (session.parentUserId match, or child-of-parent fallback),
 * so no backend change was needed. The route is additionally behind ParentGuard.
 *
 * Read-only by construction: no answer input, no submit handler, no mutation hook is
 * imported here. Messages are fetched once (no polling) — every session reachable from
 * the Reports tab is terminal, and a parent watching a live session uses LiveSessionsPage.
 *
 * Enforces TO-LL-058 (absolute '@/' imports only).
 */

import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';

import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import { MessageBubble } from '@/components/MessageBubble';
import { PassageCard } from '@/components/PassageCard';
import { PassagePanel } from '@/components/PassagePanel';
import { QuestionCycleHeader } from '@/components/QuestionCycleHeader';
import { ROUTES } from '@/constants/routes';
import { groupMessagesByQuestionCycle } from '@/lib/questionCycles';
import { getSessionMessages } from '@/services/sessions';

/**
 * ParentSessionViewPage renders a child's session transcript, read-only.
 */
export function ParentSessionViewPage() {
  const { sessionId } = useParams<{ sessionId: string }>();

  const {
    data: session,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['session', 'messages', sessionId],
    queryFn: () => getSessionMessages(sessionId!),
    enabled: Boolean(sessionId),
  });

  // Same grouping the student saw, so the parent reads the transcript exactly as it
  // was presented in the chat — question cycles in order, intro first.
  const { cycles } = useMemo(
    () => groupMessagesByQuestionCycle(session?.messages, session?.totalQuestions),
    [session?.messages, session?.totalQuestions]
  );

  const backLink = (
    <Link
      to={ROUTES.PARENT_DASHBOARD}
      className="inline-flex items-center gap-0.5 rounded-lg px-2 py-1.5 text-sm font-bold text-pebbles-500 transition-colors hover:bg-otter-sand/40 hover:text-otter-earth-800"
    >
      <ChevronLeft className="h-4 w-4" />
      <span>Back</span>
    </Link>
  );

  if (!sessionId) {
    return <ErrorState title="Session Not Found" message="No session was specified." />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-otter-paper">
        <LoadingState message="Loading session..." />
      </div>
    );
  }

  if (error || !session) {
    const message = error instanceof Error ? error.message : '';
    const isNotFound = message.includes('404') || message.includes('not found');
    return (
      <ErrorState
        title={isNotFound ? 'Session Not Found' : 'Failed to Load Session'}
        message={
          isNotFound
            ? 'This session could not be found. It may have been deleted.'
            : message || 'Unable to load this session. Please check your connection and try again.'
        }
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <div
      className="min-h-screen landscape:h-screen lg:h-screen flex flex-col bg-otter-paper"
      style={{ minHeight: 'calc(var(--vh, 1vh) * 100)' }}
    >
      {/* Header — back to the parent dashboard, never the student /sessions list */}
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-otter-sand bg-otter-paper/80 px-3 py-2 backdrop-blur-md md:px-6 md:py-3">
        <div className="flex min-w-0 items-center gap-2">
          {backLink}
          <p className="min-w-0 truncate text-sm font-medium text-otter-earth-700 md:text-base">
            {session.passageTitle || 'Session transcript'}
          </p>
        </div>
        <span className="shrink-0 rounded-full border border-otter-sand bg-white px-2.5 py-1 text-xs font-semibold text-pebbles-500">
          Read-only
        </span>
      </header>

      <div className="flex w-full flex-1 flex-col overflow-hidden landscape:flex-row lg:flex-row">
        {/* Left panel: passage (wide viewports only — mirrors the student layout) */}
        <PassagePanel passage={session.passage ?? ''} />

        {/* Right panel: the transcript */}
        <div className="flex-1 overflow-y-auto px-3 py-4 md:px-6">
          {/* Narrow viewports get the collapsible passage inline, as the student does */}
          <div className="lg:hidden landscape:hidden">
            <PassageCard passage={session.passage ?? ''} />
          </div>

          {cycles.length === 0 ? (
            <p className="py-12 text-center text-sm text-gray-500">
              This session has no messages.
            </p>
          ) : (
            cycles.map((cycle) => (
              <div key={cycle.questionNumber ?? 'intro'}>
                <QuestionCycleHeader label={cycle.label} />
                {cycle.messages.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
